import type {
  AuditionMode,
  ClipMediaMetadata,
  EditorialClipBinding,
  MediaFamily,
  MediaMember,
  MediaUnionClipLike,
} from "@/components/OmniCine/Timeline/MediaUnion";
import { resolvePlaybackProjection } from "@/components/OmniCine/Timeline/MediaUnion";
import {
  averageContinuityScore,
  deriveContinuityDiagnostics,
  summarizeContinuityDiagnostics,
} from "@/components/OmniCine/Timeline/NarrativeSonicsContinuity";
import type { NarrativeSonicsReadModel } from "@/components/OmniCine/Timeline/NarrativeSonics.types";

import { selectActiveEmotionalIntentSegments, selectActiveFxIntentNodes } from "./sonics.selectors";
import type { AuthoredSonicsState } from "./sonics.types";

export type DeriveNarrativeSonicsReadModelArgs = {
  clips: MediaUnionClipLike[];
  playheadSec: number;
  mediaFamilies: MediaFamily[];
  mediaMembers: MediaMember[];
  editorialClipBindings: EditorialClipBinding[];
  clipMediaMetadataByClipId: Record<string, ClipMediaMetadata>;
  selectedClipId: string | null;
  auditionMode: AuditionMode;
  authoredSonicsState: AuthoredSonicsState;
};

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function readClipId(clip: MediaUnionClipLike | null | undefined): string | null {
  const candidate = clip as { id?: string; clipId?: string } | null | undefined;
  const clipId = String(candidate?.id ?? candidate?.clipId ?? "").trim();
  return clipId || null;
}

function readClipStartSec(clip: MediaUnionClipLike | null | undefined): number {
  return finiteNumber((clip as { startSec?: number } | null | undefined)?.startSec) ?? 0;
}

function readClipEndSec(clip: MediaUnionClipLike | null | undefined, fallbackStartSec: number): number {
  const explicitEndSec = finiteNumber((clip as { endSec?: number } | null | undefined)?.endSec);
  if (explicitEndSec != null) return explicitEndSec;

  const durationSec = finiteNumber((clip as { durSec?: number; durationSec?: number } | null | undefined)?.durSec)
    ?? finiteNumber((clip as { durSec?: number; durationSec?: number } | null | undefined)?.durationSec)
    ?? 0;
  return fallbackStartSec + Math.max(0, durationSec);
}

export function deriveNarrativeSonicsReadModel(args: DeriveNarrativeSonicsReadModelArgs): NarrativeSonicsReadModel {
  const authoredArcSegments = selectActiveEmotionalIntentSegments(args.authoredSonicsState);
  const authoredFxNodes = selectActiveFxIntentNodes(args.authoredSonicsState);
  const playbackProjection = resolvePlaybackProjection({
    clips: args.clips,
    mediaFamilies: args.mediaFamilies,
    mediaMembers: args.mediaMembers,
    editorialClipBindings: args.editorialClipBindings,
    mediaMetadataByClipId: args.clipMediaMetadataByClipId,
    playheadSec: args.playheadSec,
    auditionMode: args.auditionMode,
    selectedClipId: args.selectedClipId,
    requestedClipId: args.selectedClipId,
  });

  const focusClipId = String(
    playbackProjection.activeAudioClipId
      ?? playbackProjection.activeVideoClipId
      ?? playbackProjection.focusClipId
      ?? args.selectedClipId
      ?? "",
  ).trim() || undefined;
  const focusClip = focusClipId
    ? args.clips.find((clip) => readClipId(clip) === focusClipId) ?? null
    : null;

  const startSec = finiteNumber(playbackProjection.activeAudio[0]?.startSec) ?? readClipStartSec(focusClip);
  const endSec = finiteNumber(playbackProjection.activeAudio[0]?.endSec) ?? readClipEndSec(focusClip, startSec);
  const durationSec = finiteNumber(playbackProjection.activeAudioDurationSec) ?? Math.max(0, endSec - startSec);
  const currentTimeSec = durationSec > 0
    ? clamp(args.playheadSec - startSec, 0, durationSec)
    : 0;

  const continuityDiagnostics = deriveContinuityDiagnostics(
    authoredArcSegments,
    authoredFxNodes,
    durationSec,
  );
  const derivedContinuityWarningCount = continuityDiagnostics.filter(
    (diagnostic) => diagnostic.status !== "coherent",
  ).length;

  return {
    focusSourceId: String(
      playbackProjection.activeAudioMemberId
        ?? playbackProjection.activeVideoMemberId
        ?? "",
    ).trim() || undefined,
    focusClipId,
    focusSequenceId: undefined,
    focusScope: args.auditionMode,
    emotionalSummary: authoredArcSegments.length > 0
      ? `${authoredArcSegments.length} authored arc segment${authoredArcSegments.length === 1 ? "" : "s"}`
      : "No authored arc intent yet",
    fxSummary: authoredFxNodes.length > 0
      ? `${authoredFxNodes.length} authored FX node${authoredFxNodes.length === 1 ? "" : "s"}`
      : "No authored FX intent yet",
    continuitySummary: summarizeContinuityDiagnostics(continuityDiagnostics),
    currentTimeSec,
    durationSec,
    activePreviewBranchId: null,
    authoredArcSegmentCount: authoredArcSegments.length,
    authoredFxNodeCount: authoredFxNodes.length,
    derivedContinuityWarningCount,
    continuityScore01: averageContinuityScore(continuityDiagnostics),
    hasAuthoredSonicsIntents: authoredArcSegments.length > 0 || authoredFxNodes.length > 0,
    hasInferredSonicsIntents: false,
    activeLensId: null,
  };
}